import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {PossiblePermissionStates} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import {ImageAndVideoRecorderStatus, RecorderMediaType, PermissionStates} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import {
  setImageAndVideoRecorderStatus,
  setMediaType,
  setMediaUrl,
  setPermission,
  setSelectedCamera,
} from '@Panels/record-image-and-video-panel/record-image-and-video-panel-reducer';
import {isIOS} from 'react-device-detect';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {Type} from '@Components/button';
import {
  getSupportedVideoMimeType,
  handleImageOrVideoUpload,
  isMobileSize,
  isOpenCustomImageAndVideoRecorderSmall,
  onUploadImageAndVideo,
} from '@Libraries/record-image-and-video.library';
import {ImageAndVideoRecorderControls} from '@Panels/record-image-and-video-panel/components/image-and-video-recorder-controls';
import {
  updateOptionsPanelHasNoBorder,
  updateOptionsPanelHasNoBorderRadius,
  updateOptionsPanelHeight,
  updateOptionsPanelStickToBottom,
} from '@Components/poster-editor/library/poster-editor-library';
import useWindowSize from '@Hooks/useWindowSize';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {POSTER_MAX_DURATION} from '@PosterWhiteboard/poster/poster.types';
import {RecordImageAndVideoPanelContent} from '@Panels/record-image-and-video-panel/components/record-image-and-video-panel-content';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import type {ElementData} from '@Libraries/add-media-library';
import type {UserMediaType, UserMediaVOResponse} from '@Libraries/user-media-library';
import {EditorMediaEvent, GA4EventParamName, GA4EventParam, trackPosterBuilderGA4Events, GA4EventName} from '@Libraries/ga-events';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './record-image-and-video-panel.module.scss';

export interface RecordImageAndVideoPanelProps {
  panelId?: string;
  onBack?(): void;
  onClose?(): void;
  recorderOpenedWithMediaType: RecorderMediaType;
  onAddSelected(item: ElementData): void;
  hideToggle?: boolean;
  collectionId?: string;
  onAddSelectedCTAText?: string;
  onStartAddSelected?: () => void;
}

const VIDEO_MINE_TYPE = 'video/mp4';
const BUFFER_MAX_VIDEO_DURATION = 2;

function RecordImageAndVideoPanel({
  hideToggle = false,
  collectionId = '',
  onAddSelectedCTAText = window.i18next.t('pmwjs_add_to_design'),
  onStartAddSelected = (): void => {},
  ...props
}: RecordImageAndVideoPanelProps): ReactElement {
  const recorderStatus = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.imageAndVideoRecorderStatus;
  });

  const selectedCamera = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.selectedCamera;
  });

  const permission = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.permission;
  });

  const mediaUrl = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.mediaUrl;
  });

  const mediaType = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.mediaType;
  });

  const isRecorderPanelClosed = useAppSelector((state) => {
    return state.panels.panelHashmap[props.panelId ?? ''].closed;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const {windowWidth} = useWindowSize();
  const previousWindowWidth = useRef(windowWidth);
  const dispatch = useAppDispatch();

  const [key, setKey] = useState(0);
  const [checkSystemPermissionsError, setCheckSystemPermissionsError] = useState(false);
  const [rempromptForPermissions, setRepromptForPermissions] = useState(false);
  const [permissionsDeniedError, setPermissionsDeniedError] = useState(false);
  const [liveRecordingDuration, setLiveRecordingDuration] = useState<number>(0);
  const [recordedDuration, setRecordedDuration] = useState<number>(0);
  const [videoInputDevices, setVideoInputDevices] = useState<Array<string>>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const customMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const panelTitle = mediaType === RecorderMediaType.VIDEO ? window.i18next.t('pmwjs_record_video') : window.i18next.t('pmwjs_capture_photo');
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(props.panelId ?? '');
  const [showMaxDurationError, setShowMaxDurationError] = useState(false);

  useEffect((): void => {
    if (liveRecordingDuration > POSTER_MAX_DURATION - BUFFER_MAX_VIDEO_DURATION) {
      setShowMaxDurationError(true);
      stopRecordingVideo();

      if (!isMobileSize(windowWidth) && !isIOS) {
        updateOptionsPanelHeight(props.panelId ?? '', `${675}px`);
      }
    } else {
      setShowMaxDurationError(false);

      if (!isMobileSize(windowWidth) && !isIOS) {
        updateOptionsPanelHeight(props.panelId ?? '', `${628}px`);
      }
    }
  }, [liveRecordingDuration]);

  useEffect(() => {
    if (props.recorderOpenedWithMediaType) {
      dispatch(setMediaType({mediaType: props.recorderOpenedWithMediaType}));
    }
    resetRecorder();

    void setAllVideoInputDeviceIds();

    return (): void => {
      resetRecorder();

      if (props.panelId === 'user-media-modal-panel') {
        const getInitialWidth = (): string => {
          if (windowWidth > 2000) {
            return '1600px';
          }
          if (windowWidth <= 2000 && windowWidth > 550) {
            return '80%';
          }
          return '600px';
        };

        dispatch(
          updatePanelSizeData({
            sizeDataId: props.panelId ?? '',
            sizeData: {
              height: '94%',
              width: getInitialWidth(),
            },
          })
        );
      }
      if (props.panelId === 'POSTER_ADD_ITEMS_PANEL_ID') {
        updateOptionsPanelHasNoBorderRadius(props.panelId, false);
        updateOptionsPanelHasNoBorder(props.panelId, false);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedCamera) {
      void getCamera();
    }
  }, [selectedCamera]);

  useEffect(() => {
    if (isRecorderPanelClosed) {
      clearPermissionsAndStream();
      dispatch(setMediaUrl(undefined));
    }
  }, [isRecorderPanelClosed]);

  useEffect((): void => {
    if (isMobileSize(windowWidth)) {
      updateOptionsPanelHeight(props.panelId ?? '', `${1000}px`);
    } else if (isIOS) {
      updateOptionsPanelHeight(props.panelId ?? '', `${5000}px`);
    } else {
      updateOptionsPanelHeight(props.panelId ?? '', `${628}px`);
    }
  }, [windowWidth]);

  useEffect(() => {
    const isDesktopResizedToMobile = (): boolean => {
      return previousWindowWidth.current >= MOBILE_MODAL_WIDTH_THRESHOLD && isMobileSize(windowWidth);
    };

    const isMobileResizedToDesktop = (): boolean => {
      return previousWindowWidth.current <= MOBILE_MODAL_WIDTH_THRESHOLD && !isMobileSize(windowWidth);
    };

    if (isDesktopResizedToMobile() || isMobileResizedToDesktop()) {
      void getCamera();
    }
    previousWindowWidth.current = windowWidth;
  }, [windowWidth]);

  useEffect(() => {
    if (permissionsDeniedError || rempromptForPermissions || checkSystemPermissionsError) {
      if (isMobileSize(windowWidth)) {
        updateOptionsPanelHeight(props.panelId ?? '', `${352}px`);
      } else {
        updateOptionsPanelHeight(props.panelId ?? '', `${544}px`);
      }
    }
  }, [permissionsDeniedError, rempromptForPermissions, checkSystemPermissionsError]);

  const onRetakeClicked = (): void => {
    setKey((prevKey) => {
      return prevKey + 1;
    });
    setLiveRecordingDuration(0);

    if (window.posterEditor?.whiteboard) {
      trackPosterBuilderGA4Events(EditorMediaEvent.EDITOR_RECORDEDMEDIA_RETAKE, {
        [GA4EventParamName.TYPE]: mediaType === RecorderMediaType.IMAGE ? GA4EventParam.IMAGE : GA4EventParam.VIDEO,
      });
    }

    dispatch(setMediaUrl(undefined));
    dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.LOADING_RECORDER}));
    dispatch(setPermission(false));
    dispatch(setSelectedCamera({selectedCamera: undefined}));
  };

  const showBackButton = (): boolean | undefined => {
    return !!props.onBack;
  };

  const getPermissionStates = async (browserPermission: string): Promise<PossiblePermissionStates> => {
    try {
      const result = await navigator.permissions.query({name: browserPermission as PermissionName});
      return result.state;
    } catch (error) {
      return `not supported`;
    }
  };

  const resetRecorder = (): void => {
    dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.LOADING_RECORDER}));
    dispatch(setSelectedCamera({selectedCamera: undefined}));
    dispatch(setMediaUrl(undefined));
  };

  const clearPermissionsAndStream = (): void => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
        stream.removeTrack(track);
      });

      setStream(null);

      customMediaRecorderRef.current = null;
    }
    setPermissionsDeniedError(false);
    setRepromptForPermissions(false);
    setCheckSystemPermissionsError(false);
  };

  const catchGetUserMediaError = async (err: any): Promise<void> => {
    console.error(err);

    const micPermission = await getPermissionStates('microphone');
    const cameraPermission = await getPermissionStates('camera');

    if (micPermission === PermissionStates.PROMPT || cameraPermission === PermissionStates.PROMPT) {
      // this happens only after user denies the prompt on safari
      setRepromptForPermissions(true);
    } else if (micPermission === PermissionStates.DENIED || cameraPermission === PermissionStates.DENIED) {
      setPermissionsDeniedError(true);
    } else {
      setCheckSystemPermissionsError(true);
    }
  };

  const getUserMediaForIOS = async (): Promise<MediaStream | undefined> => {
    const resolutions = [
      {height: 2160, width: 3840},
      {height: 1080, width: 1920},
      {height: 720, width: 1080},
      {height: window.innerHeight, width: window.innerWidth},
    ];

    let tempstream: MediaStream | undefined;

    for (const {height, width} of resolutions) {
      try {
        tempstream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            facingMode: {exact: selectedCamera && selectedCamera === 'environment' ? 'environment' : 'user'},
            width: {exact: windowWidth > MOBILE_MODAL_WIDTH_THRESHOLD ? window.innerWidth : width},
            height: {exact: windowWidth > MOBILE_MODAL_WIDTH_THRESHOLD ? window.innerHeight : height},
          },
        });
        return tempstream;
      } catch (err: any) {
        if (err.name === 'OverconstrainedError') {
          continue;
        } else {
          await catchGetUserMediaError(err);
          break;
        }
      }
    }
    return undefined;
  };

  const initialGetUserMediaCall = async (): Promise<void> => {
    let tempstream = null;

    if (isIOS) {
      tempstream = await getUserMediaForIOS();
    } else {
      try {
        tempstream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {width: {ideal: window.innerWidth}, height: {ideal: window.innerHeight}},
        });
      } catch (err: any) {
        await catchGetUserMediaError(err);
      }
    }

    if (tempstream) {
      tempstream.getTracks().forEach((track) => {
        track.stop();
        tempstream.removeTrack(track);
      });
    }
  };

  const setAllVideoInputDeviceIds = async (): Promise<void> => {
    await initialGetUserMediaCall(); // browsers require getUserMedia() to be called before enumerateDevices()

    let videoInputDeviceIds: Array<string> = [];

    if (isIOS) {
      videoInputDeviceIds = ['user', 'environment'];
    } else {
      const {mediaDevices} = navigator;
      const deviceList = await mediaDevices.enumerateDevices();
      for (const inputDevice of deviceList) {
        if (inputDevice.kind === 'videoinput') {
          videoInputDeviceIds.push(inputDevice.deviceId);
        }
      }
    }

    setVideoInputDevices(videoInputDeviceIds);
    if (!selectedCamera) {
      dispatch(setSelectedCamera({selectedCamera: videoInputDeviceIds[0]}));
    }
  };

  const getSmallScreenCameraStreamData = async (): Promise<MediaStream | undefined> => {
    const {mediaDevices} = navigator;
    let streamData: MediaStream | undefined;

    if (isIOS) {
      streamData = await getUserMediaForIOS();
    } else if (selectedCamera) {
      streamData = await mediaDevices.getUserMedia({
        audio: true,
        video: {
          deviceId: {exact: selectedCamera},
          width: {ideal: 2160},
          height: {ideal: 3840},
          aspectRatio: {exact: window.innerHeight / window.innerWidth},
        },
      });
    } else {
      streamData = await mediaDevices.getUserMedia({
        audio: true,
        video: {
          width: {ideal: 2160},
          height: {ideal: 3840},
          aspectRatio: {exact: window.innerHeight / window.innerWidth},
        },
      });
    }

    return streamData;
  };

  const getCamera = async (): Promise<void> => {
    dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.LOADING_RECORDER}));

    if (!selectedCamera) {
      return;
    }

    clearPermissionsAndStream();

    try {
      const {mediaDevices} = navigator;
      let streamData: MediaStream | undefined;

      if (isOpenCustomImageAndVideoRecorderSmall(windowWidth)) {
        streamData = await getSmallScreenCameraStreamData();
      } else if (selectedCamera) {
        streamData = await mediaDevices.getUserMedia({
          audio: true,
          video: {deviceId: {exact: selectedCamera}, width: {ideal: window.innerWidth}, height: {ideal: window.innerHeight}},
        });
      }

      if (streamData) {
        dispatch(setPermission(true));
        dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.READY_TO_RECORD}));

        setStream(streamData);
      }

      setRepromptForPermissions(false);
      setPermissionsDeniedError(false);
      setCheckSystemPermissionsError(false);
    } catch (err: any) {
      await catchGetUserMediaError(err);
    }
  };

  const captureImage = (): void => {
    if (videoRef.current) {
      dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.STOPPED}));

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;

        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        const imageDataURL = canvas.toDataURL('image/png');
        dispatch(setMediaUrl(imageDataURL));
        const capturedImageElement = new Image();
        capturedImageElement.src = imageDataURL;
      }
      canvas.remove();

      dispatch(setPermission(false));

      clearPermissionsAndStream();
    }
  };

  const startRecordingVideo = (): void => {
    let timerInterval: NodeJS.Timeout;
    let startTime = Date.now();

    if (stream && permission) {
      try {
        customMediaRecorderRef.current = new MediaRecorder(stream, {mimeType: getSupportedVideoMimeType()});
        const chunks: Blob[] = [];

        customMediaRecorderRef.current.ondataavailable = (dataEvent): void => {
          if (dataEvent.data.size > 0) {
            chunks.push(dataEvent.data);
          }
        };

        customMediaRecorderRef.current.onerror = (errorEvent): void => {
          console.error(errorEvent);
        };

        customMediaRecorderRef.current.onstart = (): void => {
          dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.RECORDING}));

          startTime = Date.now();
          timerInterval = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            setLiveRecordingDuration(Math.floor((elapsedTime / 1000) * 100) / 100);
          }, 100);
        };

        customMediaRecorderRef.current.onstop = (): void => {
          dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.STOPPED}));
          const blob = new Blob(chunks, {type: VIDEO_MINE_TYPE});
          const videoUrlToSet = URL.createObjectURL(blob);
          dispatch(setMediaUrl(videoUrlToSet));

          setRecordedDuration((Date.now() - startTime) / 1000);

          clearInterval(timerInterval);
          clearPermissionsAndStream();
        };

        customMediaRecorderRef.current.start();
      } catch (e) {
        alert((e as Error).message);
      }
    }
  };

  const stopRecordingVideo = (): void => {
    if (mediaType === RecorderMediaType.VIDEO) {
      if (customMediaRecorderRef.current && customMediaRecorderRef.current.state === 'recording') {
        customMediaRecorderRef.current.stop();
        dispatch(setImageAndVideoRecorderStatus({imageAndVideoRecorderStatus: ImageAndVideoRecorderStatus.STOPPED}));
        dispatch(setPermission(false));
      }
    }
  };

  const onUpload = (type: UserMediaType, data: Array<UserMediaVOResponse> | UserMediaVOResponse): void => {
    onUploadImageAndVideo(type, data, (item: ElementData) => {
      props.onAddSelected(item);
    });
  };

  const onAddToDesign = async (): Promise<void> => {
    if (mediaUrl) {
      onStartAddSelected();
      const response = await fetch(mediaUrl);
      const videoBlob = await response.blob();
      const fileName = mediaType === RecorderMediaType.IMAGE ? 'image.jpg' : 'video.mp4';
      const fileToUpload = new File([videoBlob], fileName, {type: videoBlob.type, lastModified: Date.now()});

      trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_RECORDED_MEDIA_ADDED, {
        [GA4EventParamName.TYPE]: mediaType === RecorderMediaType.IMAGE ? GA4EventParam.IMAGE : GA4EventParam.VIDEO,
      });

      if (window.posterEditor?.whiteboard) {
        trackPosterBuilderGA4Events(EditorMediaEvent.EDITOR_RECORDEDMEDIA_ADDED, {
          [GA4EventParamName.TYPE]: mediaType === RecorderMediaType.IMAGE ? GA4EventParam.IMAGE : GA4EventParam.VIDEO,
        });
      }

      handleImageOrVideoUpload({fileToUpload, mediaType}, onUpload, {mirror: selectedCamera !== 'environment', collectionId});

      if (isOpenCustomImageAndVideoRecorderSmall(windowWidth)) {
        dispatch(closeModal(MODALS_IDS.POSTER_EDITOR_ADD_ITEM_MODAL));
      }

      customOnClose();
    }
  };

  const getFooterPrimaryActions = (): Array<ReactElement> => {
    return [
      <PanelFooterPrimaryDefaultAction
        onClick={() => {
          onRetakeClicked();
        }}
        type={Type.SECONDARY}
        key="btn-record-again"
        text={mediaType === RecorderMediaType.IMAGE ? window.i18next.t('pmwjs_retake_photo') : window.i18next.t('pmwjs_retake_video')}
      />,
      <PanelFooterPrimaryDefaultAction className="spacing-m-l-2" onClick={onAddToDesign} key="btn-add-to-design" text={onAddSelectedCTAText} />,
    ];
  };

  const getFooter = (): ReactElement | null => {
    if (permissionsDeniedError || rempromptForPermissions || checkSystemPermissionsError) {
      return null;
    }
    if (recorderStatus === ImageAndVideoRecorderStatus.STOPPED) {
      return <PanelFooter className={`${styles.noTopBorder} ${styles.footerCTAs} spacing-p-t-3`} primaryActions={getFooterPrimaryActions()} />;
    }
    return (
      <div
        className={`spacing-m-t-5 ${styles.footer} ${styles.noTopBorder} ${recorderStatus === ImageAndVideoRecorderStatus.LOADING_RECORDER ? `${styles.collapsedFooter} _invisible` : ''}`}
      >
        <ImageAndVideoRecorderControls
          videoInputDevices={videoInputDevices}
          duration={liveRecordingDuration}
          getCamera={getCamera}
          captureImage={captureImage}
          startRecordingVideo={startRecordingVideo}
          stopRecordingVideo={stopRecordingVideo}
          hideToggle={hideToggle}
        />
      </div>
    );
  };

  const customOnClose = (): void => {
    clearPermissionsAndStream();
    resetRecorder();
    if (props.panelId === 'user-media-modal-panel') {
      updateOptionsPanelStickToBottom(props.panelId, true);
    } else if (props.panelId === 'POSTER_ADD_ITEMS_PANEL_ID') {
      updateOptionsPanelHasNoBorderRadius(props.panelId, false);
      updateOptionsPanelHasNoBorder(props.panelId, false);
    }

    onClose();
  };

  const getImageAndVideoRecordingPanelContent = (): ReactElement => {
    return (
      <RecordImageAndVideoPanelContent
        onClose={onClose}
        panelId={props.panelId}
        key={key}
        captureImage={captureImage}
        customOnClose={customOnClose}
        getCamera={getCamera}
        onAddToDesign={onAddToDesign}
        onRetake={onRetakeClicked}
        checkSystemPermissionsError={checkSystemPermissionsError}
        permissionsDeniedError={permissionsDeniedError}
        recordingDuration={liveRecordingDuration}
        rempromptForPermissions={rempromptForPermissions}
        setAllVideoInputDeviceIds={setAllVideoInputDeviceIds}
        showMaxDurationError={showMaxDurationError}
        startRecordingVideo={startRecordingVideo}
        stopRecordingVideo={stopRecordingVideo}
        stream={stream}
        videoInputDevices={videoInputDevices}
        videoRef={videoRef}
        hideToggle={hideToggle}
      />
    );
  };

  const getGeneralImageAndVideoRecordingPanel = (): ReactElement => {
    return (
      <Panel
        ref={containerRef}
        panelHeader={
          <PanelHeader
            title={`${panelTitle}`}
            onClose={customOnClose}
            showCloseButton
            showBackButton={showBackButton()}
            onBackButton={(): void => {
              if (props.onBack) {
                props.onBack();
                clearPermissionsAndStream();
                if (props.panelId === 'user-media-modal-panel') {
                  updateOptionsPanelStickToBottom(props.panelId, true);
                } else if (props.panelId === 'POSTER_ADD_ITEMS_PANEL_ID') {
                  updateOptionsPanelHasNoBorderRadius(props.panelId, false);
                  updateOptionsPanelHasNoBorder(props.panelId, false);
                }
              }
            }}
          />
        }
        panelContent={getImageAndVideoRecordingPanelContent()}
        panelFooter={getFooter()}
      />
    );
  };

  const getMobileImageAndVideoRecordingPanel = (): ReactElement => {
    return <Panel ref={containerRef} panelContent={getImageAndVideoRecordingPanelContent()} />;
  };

  const getRecordImageAndVideoRecordingPanel = (): ReactElement => {
    if (isOpenCustomImageAndVideoRecorderSmall(windowWidth)) {
      return getMobileImageAndVideoRecordingPanel();
    }

    return getGeneralImageAndVideoRecordingPanel();
  };

  return getRecordImageAndVideoRecordingPanel();
}

export default RecordImageAndVideoPanel;
